import { userMessagesSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector, useDebounce } from '@common-reducers/hooks/store.hook';
import { useAnalyticsService } from "@common-reducers/hooks/use-analytics-service";
import { AnalyticsService } from "@common-services/analytics-service";
import { DeleteMessageButton } from "@common/components/delete-message-button/delete-message-button";
import { addScheduledMessageButtonActions, selectScheduleRecipients } from "@common/reducers/AddScheduledMessageButtonReducer";
import { handleAddScheduledMessageCloseThunk, handleRecurringMessageEditThunk, scheduleMessage, ScheduleMessageThunkArg } from "@common/thunks/UserMessagesThunks";
import { ensureClientIdempotencyKey } from "@common/utils/client-idempotency-key";
import { toastDBCreateError } from "@common/utils/db-create-error-toast";
import { createSubmitGuard } from "@common/utils/submit-guard";
import { useIsMobile } from "@common/hooks/use-is-mobile";
import { setDueDate } from "@common/reducers/ScheduledMessageEditorReducer";
import { CircularProgress } from '@mui/material';
import { AlertCircle, CalendarClock, Clock, Info } from "lucide-react";
import styles from "./compose-dialog.module.scss";
import moment from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { resolvePastTimeState } from './past-time-gate';
import { canScheduleSend } from './schedule-send-gate';
import { useNowTicker } from './use-now-ticker';
import { hasStatusRecipient } from '@common/scheduling/status-recipient';
import { shouldWarnLocalSend } from './local-send-warning';
import { selectConnectionKind } from "@common-reducers/ConnectedEnginesSlice";
// Opens /connections in a new tab — this draft is not lost.
import { navigateToEngines } from "@common/utils/navigation.util";


// Hook for managing poll validation
const usePollValidation = (message: any) => {
    return useCallback(() => {
        function haveSimilarOptions() {
            const options = (message?.poll?.options ?? []).filter((option: { text: string }) => option.text !== '');
            const uniqueOptions = [...new Set(options.map((option: { text: string }) => option.text))];
            return options.length !== uniqueOptions.length;
        }

        return message?.isPoll &&
            message?.poll?.question?.length > 0 &&
            message?.poll?.options?.length > 1 &&
            message?.poll?.options?.filter((o: { text: string }) => o.text.length > 0).length > 1 &&
            !haveSimilarOptions();
    }, [message?.isPoll, message?.poll?.question, message?.poll?.options]);
};


export default function AddScheduledMessageDialogFooter() {
    const { t } = useTranslation(namespace);
    const isMobile = useIsMobile();
    const analyticsService: AnalyticsService = useAnalyticsService();
    const dispatch = useAppDispatch();
    const messageDraft = useAppSelector(state => state.AddScheduledMessageButtonReducer.messageDraft);
    const message = useAppSelector(state => state.AddScheduledMessageButtonReducer.message);
    // FE#230: the recipient list is the single source of truth for BOTH the
    // Schedule-Send gate and the persisted payload. Read it through the memoized
    // selector — never from message.contactList (a re-init can wipe that) or a
    // single-contact placeholder.
    const recipients = useAppSelector(selectScheduleRecipients);
    // No always-on connection: a send due later goes out only if WhatsApp Web is open then.
    const connectionKind = useAppSelector(selectConnectionKind);
    const [isUpdatingLocalState, setIsUpdatingLocalState] = useState(false);
    // BE#372/#231 — synchronous single-flight guard. A useState flag cannot block a synchronous
    // double-click (the 2nd click reads the pre-setState value), so the actual block lives in this
    // ref-held guard; isUpdatingLocalState only drives the visual disabled affordance. One guard per
    // mount → a fresh dialog session starts unlocked.
    const submitGuardRef = useRef(createSubmitGuard());
    const messageList = useAppSelector(state => userMessagesSelectByQuery(state.DBUserMessageReducer, {
        $or: [{ deleted: { $exists: false } }, { deleted: false }],
    }));
    
    // Check if popup was opened from calendar sidebar
    const openedFromContext = useAppSelector(state => state.AddScheduledMessageButtonReducer.openedFromContext);
    const isFileUploading = useAppSelector(state => state.ScheduledMessageEditorReducer.isFileUploading);
    const timeInputInvalid = useAppSelector(state => state.ScheduledMessageEditorReducer.timeInputInvalid);

    const debouncedMessage = useDebounce(messageDraft, 300);

    const isValidPoll = usePollValidation(debouncedMessage);

    const handleClose = () => {
        dispatch(handleAddScheduledMessageCloseThunk());
    };

    const handleDelete = (messageToDelete: any) => {
        handleClose();
    };

    const scheduleSend = () => {
        // BE#372/#231 — block a synchronous double-click at the top. tryAcquire() flips instantly
        // (unlike setIsUpdatingLocalState, which the 2nd click of a double-click would not yet see),
        // so only the first click proceeds. Released only on the branch that keeps the dialog open;
        // the create/close branches unmount the footer, discarding this guard.
        if (!submitGuardRef.current.tryAcquire()) return;
        setIsUpdatingLocalState(true); // visual disabled affordance

        // The stable idempotency key is stamped on the draft at dialog-OPEN (see the reducer's
        // edit* actions), so both clicks of a double-click read the SAME key here and the backend
        // dedupes a slipped-through second create onto the first. ensure() is a defensive fallback
        // for the (unexpected) case the draft has no key yet.
        // #1136: the dialog closes right away, so a schedule that did not store must say so
        // (the create used to "succeed" with the error as its result and nothing was shown).
        const scheduleAndReportFailure = (arg: ScheduleMessageThunkArg): void => {
            dispatch(scheduleMessage(arg)).unwrap().catch((error: unknown) => {
                toastDBCreateError(error, t('scheduleMessage.notScheduled', 'Your message was not scheduled.'));
            });
        };

        const clientIdempotencyKey = ensureClientIdempotencyKey(messageDraft.clientIdempotencyKey);
        if (clientIdempotencyKey !== messageDraft.clientIdempotencyKey) {
            dispatch(addScheduledMessageButtonActions.updateMessageDraft({ clientIdempotencyKey }));
        }

        // Find the original message if we're editing one (use message._id for lookup, but schedule with draft data)
        const originalMessage = message._id ? messageList.find((m) => m._id === message._id) : null;

        // FE#230: persist EXACTLY the recipient selection held in Redux. Every
        // selected group/contact is fanned out one-per-recipient downstream
        // (scheduleMessageTool + per-recipient idempotency key). No placeholder
        // fallback — an empty selection is blocked by the Schedule-Send gate.
        const contactList = recipients;

        const current = {
            ...messageDraft, // Use draft for scheduling to avoid race conditions
            _id: message._id, // Preserve ID for editing scenarios
            clientIdempotencyKey, // stable per-dialog key → backend create dedupe
            contactList: contactList, // Use all selected contacts
            ...(!messageDraft.isRecurring && {
                rruleset: undefined,
                recurenceUnit: undefined,
                recurrenceQuantity: undefined,
            })
        };

        // Check if we're editing a recurring message
        if (message._id && originalMessage?.isRecurring) {
            // If opened from calendar sidebar, treat as regular message update since we've already calculated next occurrence
            if (openedFromContext === 'calendar-sidebar') {

                // For calendar sidebar, use scheduleMessage which handles both recurring and regular messages
                scheduleAndReportFailure(current as ScheduleMessageThunkArg);
                handleClose();
            } else {
                // Show confirmation dialog for other contexts. No create happens here and the dialog
                // stays open, so release the guard + clear the visual lock (the confirmation dialog
                // has its own submit); otherwise a cancelled confirmation would leave Send disabled.
                dispatch(addScheduledMessageButtonActions.setConfirmationDialog(true));
                submitGuardRef.current.release();
                setIsUpdatingLocalState(false);
            }
        } else {
            scheduleAndReportFailure(current as ScheduleMessageThunkArg);
            handleClose();
        }
    };


    // A message may not be scheduled into the past.
    //
    // The dialog has warned about a past send time for a while and then let Send
    // through anyway — so someone who read the warning, agreed with it and
    // pressed Send got the very thing the warning said would happen. The decision
    // is ONE pure predicate shared with the summary strip (past-time-gate.ts), so
    // the sentence on screen and the state of the button cannot disagree; and it
    // reads a TICKING clock, because a dialog left open across the chosen time
    // changes state with no user input at all.
    //
    // Recurring drafts are exempt: for a series the dueDate is the anchor and the
    // next send comes from the rule. See past-time-gate.ts.
    const now = useNowTicker();
    const { block: dueDateInThePast } = resolvePastTimeState({
        dueDate: messageDraft?.dueDate,
        isRecurring: messageDraft?.isRecurring,
        rruleset: messageDraft?.rruleset,
        now,
    });

    const canSchedule = useMemo(() => canScheduleSend({
        isFileUploading,
        timeInputInvalid,
        dueDateInThePast,
        hasValidDueDate: !!messageDraft?.dueDate && moment(messageDraft?.dueDate).isValid(),
        // BE#657 — the date must have been EXPLICITLY picked by the user. A draft that
        // merely carries a seeded ~now dueDate (never chosen) is NOT sendable — the
        // chokepoint that stopped the "fired days early" sends across entry points.
        dueDateSetByUser: !!messageDraft?.dueDateSetByUser,
        recipientCount: recipients.length,
        hasContentOrPoll:
            (!messageDraft.isPoll && ((messageDraft.message?.length ?? 0) > 0 || !!messageDraft?.asset?.url || !!messageDraft?.imageId)) ||
            (!!messageDraft.isPoll && isValidPoll()),
    }), [isFileUploading, timeInputInvalid, dueDateInThePast, messageDraft?.dueDate, messageDraft?.dueDateSetByUser, messageDraft.isPoll, messageDraft.message?.length, messageDraft?.asset?.url, messageDraft?.imageId, recipients.length, isValidPoll]);

    // Why Schedule Send is off, and how loudly to say it.
    //
    // `kind` exists because these reasons are not equally urgent. "Add a
    // recipient" / "write a message" is simply the dialog not being finished
    // yet — shouting it in amber at someone who opened the dialog one second ago
    // reads as an error they did not make. A time that has passed, or a date/time
    // that cannot be read, is something they DID and must fix: that one gets the
    // tinted callout (and, for the past time, the one-click remedy).
    type BlockerKind = 'pastTime' | 'invalid' | 'incomplete' | 'needDate';
    const disabledReason = useMemo((): { text: string; kind: BlockerKind } | null => {
        const incomplete = (text: string) => ({ text, kind: 'incomplete' as const });
        const invalid = (text: string) => ({ text, kind: 'invalid' as const });
        // BE#657 m1 — a missing/unconfirmed date gets its own kind so the callout can
        // offer "set it to now" (a valid remedy here), unlike an invalid time input.
        const needDate = (text: string) => ({ text, kind: 'needDate' as const });
        if (isFileUploading) return incomplete(t('scheduledMessage.validation.fileUploading'));
        if (isUpdatingLocalState) return incomplete(t('scheduledMessage.processing'));
        if (recipients.length === 0) return incomplete(t('scheduledMessage.validation.addRecipient'));
        if (timeInputInvalid) return invalid(t('scheduledMessage.validation.enterValidTime'));
        // BE#657 — treat a never-picked date the same as a missing one: prompt the user
        // to choose a time (a seeded ~now value is present but was not an explicit pick).
        if (!messageDraft?.dueDate || !moment(messageDraft?.dueDate).isValid() || !messageDraft?.dueDateSetByUser) return needDate(t('scheduledMessage.validation.selectValidDate'));
        // Ordered AFTER "is there a valid date at all" and BEFORE the content
        // checks: telling someone their time has passed while they have not
        // picked one is nonsense, and a past time is the more urgent complaint
        // than a missing poll option once a time exists.
        if (dueDateInThePast) return {
            text: t('scheduledMessage.validation.timeAlreadyPassed', 'That time has already passed. Pick a new time, or set it to now.'),
            kind: 'pastTime',
        };
        if (messageDraft.isPoll) {
            if (!messageDraft?.poll?.question?.length) return incomplete(t('scheduledMessage.validation.enterPollQuestion'));
            const validOptions = messageDraft?.poll?.options?.filter(o => o.text?.length > 0) || [];
            if (validOptions.length < 2) return incomplete(t('scheduledMessage.validation.addPollOptions'));
            const uniqueOptions = [...new Set(validOptions.map(o => o.text))];
            if (validOptions.length !== uniqueOptions.length) return invalid(t('scheduledMessage.validation.uniquePollOptions'));
        } else {
            if (!messageDraft.message?.length && !messageDraft?.asset?.url && !messageDraft?.imageId) {
                return incomplete(t('scheduledMessage.validation.enterMessageOrAttachment'));
            }
        }
        return null;
    }, [isFileUploading, isUpdatingLocalState, timeInputInvalid, dueDateInThePast, recipients.length, messageDraft?.dueDate, messageDraft?.dueDateSetByUser, messageDraft.isPoll, messageDraft.message?.length, messageDraft?.asset?.url, messageDraft?.imageId, messageDraft?.poll?.question, messageDraft?.poll?.options, t]);

    // The warning has always offered "or set it to now" — this is that option
    // as a control instead of an instruction. It writes through the same editor
    // action the date/time pickers use, so the pickers, the summary strip and
    // this gate all move together (the editor state syncs into the draft).
    const handleSetToNow = () => {
        dispatch(setDueDate(moment().utc().toISOString()));
    };

    const showBlocker = !canSchedule && !isUpdatingLocalState && !!disabledReason;
    const warnLocalSend = !showBlocker && shouldWarnLocalSend({ kind: connectionKind, dueDate: messageDraft?.dueDate, now, isRecurring: messageDraft?.isRecurring });
    const isLoudBlocker = disabledReason?.kind === 'pastTime' || disabledReason?.kind === 'invalid' || disabledReason?.kind === 'needDate';


    return (
        <div className={`${styles.root} ${styles.footer} ${isMobile ? styles.footerMobile : ''}`}>
            {/* The reason Send is off, stated INLINE.
                It used to live in a hover-only MUI Tooltip attached to the
                disabled button: unreachable on touch, and invisible to anyone
                who does not think to hover a control that already looks dead.
                The text itself is unchanged — `disabledReason` is the same
                computed string; it is simply shown now. */}
            {showBlocker && disabledReason && (isLoudBlocker || isMobile) && (
                isLoudBlocker ? (
                    <div className={styles.callout} role="alert">
                        <span className={styles.calloutMark} aria-hidden="true">
                            <AlertCircle size={16} />
                        </span>
                        <span className={styles.calloutText}>{disabledReason.text}</span>
                        {(disabledReason.kind === 'pastTime' || disabledReason.kind === 'needDate') && (
                            <button
                                type="button"
                                onClick={handleSetToNow}
                                className={styles.calloutAction}
                            >
                                <Clock size={14} aria-hidden="true" />
                                <span>{t('scheduledMessage.validation.setToNow', 'Set to now')}</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={styles.hint} role="status">
                        <span className={styles.hintMark} aria-hidden="true">
                            <Info size={14} />
                        </span>
                        <span>{disabledReason.text}</span>
                    </div>
                )
            )}

            {warnLocalSend && (
                <div className={styles.hint} role="status">
                    <span className={styles.hintMark} aria-hidden="true">
                        <Info size={14} />
                    </span>
                    <span>
                        {t('scheduledMessage.connectionAlert.browserOpenWarning')}{' '}
                        <button type="button" onClick={navigateToEngines} className={styles.hintLink}>
                            {t('scheduledMessage.connectionAlert.connectDevice')}
                        </button>
                    </span>
                </div>
            )}

            <div className={styles.footerRow}>
                {/* Delete — only when editing an existing message. A red MARK,
                    never a red fill or a red border (the same rule the failed
                    calendar chip follows). */}
                {message._id && (
                    <DeleteMessageButton
                        message={message}
                        onDelete={handleDelete}
                        className={`${styles.btn} ${styles.btnDanger}`}
                        showDeleteString={!isMobile}
                    />
                )}

                {/* Desktop: the quiet "not finished yet" note sits in the button
                    row itself, beside the Send it explains — a soft aside, not a
                    row of its own. (Mobile keeps it above: the buttons fill the
                    row there.) */}
                {showBlocker && disabledReason && !isLoudBlocker && !isMobile && (
                    <div className={styles.hint} role="status">
                        <span className={styles.hintMark} aria-hidden="true">
                            <Info size={14} />
                        </span>
                        <span>{disabledReason.text}</span>
                    </div>
                )}

                <div className={styles.footerSpacer} />

                <button
                    type="button"
                    onClick={handleClose}
                    className={`${styles.btn} ${styles.btnGhost} ${isMobile ? styles.btnMobile : ''}`}
                >
                    {t('messageScheduling.cancel')}
                </button>

                <button
                    type="button"
                    disabled={!canSchedule || isUpdatingLocalState}
                    onClick={!canSchedule || isUpdatingLocalState ? undefined : () => {
                        analyticsService.event("first_action", {
                            category: "firstAction",
                            action: "first action",
                        });
                        scheduleSend();
                    }}
                    className={[
                        styles.btn,
                        (!canSchedule || isUpdatingLocalState) ? styles.btnPrimaryDisabled : styles.btnPrimary,
                        isMobile ? styles.btnMobile : '',
                    ].filter(Boolean).join(' ')}
                >
                    {isUpdatingLocalState ? (
                        <span className={styles.spinner}>
                            <CircularProgress size={14} color="inherit" />
                            <span>{t('messageScheduling.processing')}</span>
                        </span>
                    ) : (
                        <>
                            <CalendarClock size={15} aria-hidden="true" />
                            <span>{hasStatusRecipient(recipients) ? t('scheduledMessage.status.scheduleButton') : t('messageScheduling.scheduleSend')}</span>
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}
